import { ContractVersionPageContractVersionFragment } from 'generated/resources/graphql'
import sortBy from 'lodash/sortBy'
import moment from 'moment'

import { stringToDate } from 'modules/core/lib/dates'

import type GraphqlContract from 'modules/contracts/lib/graphqlContract'

export const ContractSchedule = {
  expired: 'expired',
  ongoing: 'ongoing',
  upcoming: 'upcoming',
} as const

export type ContractSchedule = (typeof ContractSchedule)[keyof typeof ContractSchedule]

export const getContractVersionSchedule = (
  contractVersion: ContractVersionPageContractVersionFragment,
  contractVersions: ContractVersionPageContractVersionFragment[]
): ContractSchedule => {
  const now = moment()
  const referenceDate = stringToDate(contractVersion.effectiveOn, 'backend')

  if (referenceDate.isAfter(now)) return ContractSchedule.upcoming

  const isTerminated =
    !!contractVersion.endsOn && stringToDate(contractVersion.endsOn, 'backend').isBefore(now)
  if (referenceDate.isBefore(now) && isTerminated) return ContractSchedule.expired

  const sortedContractVersions = sortBy(contractVersions, (version) =>
    stringToDate(version.effectiveOn, 'backend')
  )

  const contractIndex = sortedContractVersions.findIndex(
    (version) => version.id === contractVersion.id
  )
  const nextContractVersion = sortedContractVersions[contractIndex + 1]

  if (contractIndex === 0) {
    if (
      !nextContractVersion ||
      stringToDate(nextContractVersion.effectiveOn, 'backend').isAfter(now)
    )
      return ContractSchedule.ongoing
    return ContractSchedule.expired
  }

  if (nextContractVersion && stringToDate(nextContractVersion.effectiveOn, 'backend').isBefore(now))
    return ContractSchedule.expired

  return ContractSchedule.ongoing
}

export const hasOngoingOrUpcomingContract = (contractVersions: GraphqlContract[]): boolean => {
  const ongoingContract = contractVersions.find(
    (version) => version.isOngoing() || version.isUpcoming()
  )

  return !!ongoingContract
}

export const hasOngoing = (contractVersions: GraphqlContract[]): boolean => {
  const ongoingContract = contractVersions.find((version) => version.isOngoing())

  return !!ongoingContract
}

export const findOngoingContractVersion = (
  contractVersions: ContractVersionPageContractVersionFragment[]
): ContractVersionPageContractVersionFragment | undefined => {
  return contractVersions.find(
    (version) => getContractVersionSchedule(version, contractVersions) === ContractSchedule.ongoing
  )
}
